/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { getHeaders, handleResponse, withJsonFormat } from '@opengeoweb/api';
import type {
  Coverage,
  CoverageCollection,
  VerticalProfileDomain,
} from '@opengeoweb/shared';
import type {
  CollectionDetails,
  LocationsResponseFromBE,
  SoundingsInstance,
  SoundingsInstances,
} from './types';

export const getCollectionLocations = async (
  collectionId: string,
  baseUrl: string,
): Promise<LocationsResponseFromBE> =>
  fetch(withJsonFormat(`${baseUrl}/collections/${collectionId}/locations`), {
    headers: getHeaders(),
  }).then(handleResponse) as Promise<LocationsResponseFromBE>;

export const getInstanceLocations = async (
  collectionId: string,
  instanceId: string,
  baseUrl: string,
): Promise<LocationsResponseFromBE> =>
  fetch(
    withJsonFormat(
      `${baseUrl}/collections/${collectionId}/instances/${instanceId}/locations`,
    ),
    { headers: getHeaders() },
  ).then(handleResponse) as Promise<LocationsResponseFromBE>;

export const getCollectionInstances = async (
  collectionId: string,
  baseUrl: string,
): Promise<SoundingsInstances> =>
  fetch(withJsonFormat(`${baseUrl}/collections/${collectionId}/instances`), {
    headers: getHeaders(),
  }).then(handleResponse) as Promise<SoundingsInstances>;

export const getCollectionDetails = async (
  collectionId: string,
  baseUrl: string,
): Promise<CollectionDetails> =>
  fetch(withJsonFormat(`${baseUrl}/collections/${collectionId}`), {
    headers: getHeaders(),
  }).then(handleResponse) as Promise<CollectionDetails>;

export const getInstanceDetails = async (
  collectionId: string,
  instanceId: string,
  baseUrl: string,
): Promise<SoundingsInstance> =>
  fetch(
    withJsonFormat(
      `${baseUrl}/collections/${collectionId}/instances/${instanceId}`,
    ),
    {
      headers: getHeaders(),
    },
  ).then(handleResponse) as Promise<SoundingsInstance>;

export const getEdrCollectionForLocation = async (
  collectionId: string,
  baseUrl: string,
  instanceId: string | undefined,
  locationId: string,
  parameters: string[],
  datetime: string,
  verticalRange: string,
): Promise<
  CoverageCollection<VerticalProfileDomain> | Coverage<VerticalProfileDomain>
> => {
  const url = new URL(
    `${baseUrl}/collections/${collectionId}${instanceId ? `/instances/${instanceId}` : ''}/locations/${locationId}`,
  );
  url.searchParams.append('parameter-name', parameters.join(','));
  url.searchParams.append('datetime', datetime);
  url.searchParams.append('z', verticalRange);
  url.searchParams.append('f', 'CoverageJSON');
  return fetch(url.toString(), {
    headers: getHeaders(),
  }).then(handleResponse) as Promise<
    CoverageCollection<VerticalProfileDomain> | Coverage<VerticalProfileDomain>
  >;
};

export const getEdrCollectionForPosition = async (
  collectionId: string,
  baseUrl: string,
  instanceId: string | undefined,
  coordinates: [number, number],
  parameters: string[],
  datetime: string,
  verticalRange: string,
): Promise<
  CoverageCollection<VerticalProfileDomain> | Coverage<VerticalProfileDomain>
> => {
  const url = new URL(
    `${baseUrl}/collections/${collectionId}${instanceId ? `/instances/${instanceId}` : ''}/position`,
  );
  url.searchParams.append(
    'coords',
    `POINT(${coordinates[0]} ${coordinates[1]})`,
  );
  url.searchParams.append('parameter-name', parameters.join(','));
  url.searchParams.append('datetime', datetime);
  if (verticalRange) {
    url.searchParams.append('z', verticalRange);
  }
  url.searchParams.append('f', 'CoverageJSON');
  return fetch(url.toString(), {
    headers: getHeaders(),
  }).then(handleResponse) as Promise<
    CoverageCollection<VerticalProfileDomain> | Coverage<VerticalProfileDomain>
  >;
};

/**
 * EDR radius query to fetch data from grid points within a radius around a center point.
 * The radius should be set close to the source grid resolution to capture nearby grid points.
 * Returns a CoverageCollection with coverages for each grid point within the radius.
 */
export const getEdrCollectionForRadius = async (
  collectionId: string,
  baseUrl: string,
  instanceId: string | undefined,
  centerCoordinates: [number, number],
  radiusKm: number,
  parameters: string[],
  datetime: string,
  verticalRange: string,
): Promise<
  CoverageCollection<VerticalProfileDomain> | Coverage<VerticalProfileDomain>
> => {
  const url = new URL(
    `${baseUrl}/collections/${collectionId}${instanceId ? `/instances/${instanceId}` : ''}/radius`,
  );
  url.searchParams.append(
    'coords',
    `POINT(${centerCoordinates[0]} ${centerCoordinates[1]})`,
  );
  // EDR spec uses 'within' parameter with value and unit, e.g., "5:km"
  url.searchParams.append('within', `${radiusKm}`);
  url.searchParams.append('within-units', 'km');
  url.searchParams.append('parameter-name', parameters.join(','));
  url.searchParams.append('datetime', datetime);
  if (verticalRange) {
    url.searchParams.append('z', verticalRange);
  }
  url.searchParams.append('f', 'CoverageJSON');
  return fetch(url.toString(), {
    headers: getHeaders(),
  }).then(handleResponse) as Promise<
    CoverageCollection<VerticalProfileDomain> | Coverage<VerticalProfileDomain>
  >;
};
